/**
 * Textvarianten der Immobilien-Erfassung — Kundenansprache vs. interne Sicht.
 *
 * Dieselbe Maske läuft an vier Mount-Punkten: einmal als öffentliches
 * Kundenformular (`chrome: "public"`) und dreimal intern, wo ein Mitarbeiter
 * die Angaben **für** den Kunden erfasst. Sätze wie „Nutzen Sie das Objekt
 * selbst?" oder „wir senden Ihnen einen Upload-Link nach Eingang des
 * Formulars" sind intern schlicht falsch.
 *
 * Bewusst **kein** i18n-Framework: es geht um zwei Varianten desselben
 * deutschen Textes. Der Mechanismus ist absichtlich winzig — ein Record mit
 * `{ kunde, intern }` und ein Zugriff. Steps, deren Texte noch nicht
 * umgestellt sind, bleiben unverändert; sie ziehen in ihren eigenen PRs nach.
 */

export type Ansprache = "kunde" | "intern";

export type TextVariante = { kunde: string; intern: string };

/**
 * Wer liest den Text?
 *
 * Nicht am Chrome-Modus allein ablesbar: `RisikoanalyseDetailFormPublic`
 * rendert dieselbe Maske ebenfalls mit `chrome="embedded"`, ist aber der
 * **unauthentifizierte Kundenweg** über einen Token. Verlässlich ist erst die
 * Kombination — intern ist nur, wo auch der Kontaktbestand offensteht
 * (`contactLookup`), und das setzt ausschließlich das authentifizierte
 * Backoffice.
 */
export const anspracheFor = ({
  chrome,
  contactLookup,
}: {
  chrome: "public" | "embedded";
  contactLookup: boolean;
}): Ansprache => (chrome === "public" || !contactLookup ? "kunde" : "intern");

export const IMMO_TEXTE = {
  /** Anhang an das Upload-Platzhalter-Label. */
  uploadHinweis: {
    kunde: "wir senden Ihnen einen Upload-Link nach Eingang des Formulars.",
    intern: "kann nach dem Speichern an der Immobilie hinterlegt werden.",
  },
  beschreibungAnschrift: {
    kunde:
      "Bitte geben Sie die Anschrift des Risikos an. Mit den Verlinkungen unten können Sie das Objekt direkt in Google Earth oder TIM-online anschauen.",
    intern:
      "Anschrift des Risikos. Über die Verlinkungen unten lässt sich das Objekt direkt in Google Earth oder TIM-online ansehen.",
  },
  // Gutachten und Finanzierung stehen seit der neuen Gliederung in eigenen
  // Schritten — der Text darf sie hier nicht mehr ankündigen.
  beschreibungEigentum: {
    kunde:
      "Wem gehört das Objekt, und welche Rechte liegen darauf? Eigentum, Nießbrauch und Erbbaurecht.",
    intern:
      "Eigentumsverhältnisse und Rechte am Objekt: Eigentümer und Anteile, Nießbrauch, Erbbaurecht.",
  },
  beschreibungGutachten: {
    kunde:
      "Liegt Ihnen ein Wertgutachten zum Objekt vor? Wenn ja: von wem, von wann und aus welchem Anlass.",
    intern:
      "Wertgutachten zum Objekt: Ersteller, Erstellungsdatum, Bewertungsstichtag und Anlass.",
  },
  beschreibungFinanzierung: {
    kunde:
      "Ist das Objekt finanziert? Dann benötigen wir das Institut und die Darlehensnummer — Banken verlangen in der Regel einen Sicherungsschein.",
    intern:
      "Darlehen und Sicherungsrechte: finanzierendes Institut, Darlehensnummer, Ablauf und Art der Sicherung.",
  },
  beschreibungBeteiligte: {
    kunde:
      "Wer nutzt oder betreut das Objekt außer Ihnen? Verwaltung, Mieter, Pächter, Betreiber und weitere Beteiligte.",
    intern:
      "Alle weiteren Personen und Unternehmen am Objekt: Verwaltung, Mieter, Pächter, Betreiber, Facility Management und sonstige Verbundene.",
  },
  beschreibungVersicherungsbedarf: {
    kunde: "Wofür wünschen Sie Versicherungsschutz — und was ist schon versichert?",
    intern: "Bedarf je Sparte: gewünscht, besteht bereits oder kein Bedarf.",
  },
  beschreibungUnterlagen: {
    kunde: "Welche Unterlagen können Sie nachreichen?",
    intern: "Welche Unterlagen liegen vor bzw. werden nachgereicht?",
  },
  beschreibungSummary: {
    kunde: "Überprüfen Sie Ihre Eingaben pro Gebäude.",
    intern: "Erfasste Angaben je Gebäude prüfen.",
  },
  /**
   * Einleitung über der Unterlagen-Liste an den Stellen, an denen dort NICHT
   * hochgeladen werden kann (Modus `disabled` — beide Kundenwege, siehe
   * `uploadBuffer.tsx`). Dort steht je Dokument weiterhin das Auswahlfeld, und
   * die Datei folgt erst nach dem Absenden über einen Link — genau das kündigt
   * dieser Satz an.
   */
  unterlagenIntro: {
    kunde:
      "Bitte geben Sie an, ob die folgenden Unterlagen vorliegen. Bei „anbei“ senden wir Ihnen nach Absendung einen Upload-Link.",
    intern: "Bitte angeben, ob die folgenden Unterlagen vorliegen.",
  },
  /**
   * Einleitung über derselben Liste, wenn je Dokument ein echter Hochlade-
   * Bereich darunter steht (Modus `buffer`/`immediate` — heute nur der interne
   * Weg über `NewRiskPage`).
   *
   * ACHTUNG (E11): Welche der beiden Fassungen erscheint, entscheidet die
   * Upload-Fähigkeit, NICHT die Ansprache. Wer hier nach `ansprache` abbiegt,
   * verspricht dem Kunden einen Bereich, den es unter dem Absatz nicht gibt —
   * dort rendert `UploadField` im Modus `disabled` nur eine Hinweiszeile.
   */
  unterlagenIntroUpload: {
    kunde:
      "Bitte laden Sie die folgenden Unterlagen hoch, soweit sie vorliegen. Jedes Dokument hat seinen eigenen Bereich; was fehlt, bleibt einfach leer.",
    intern:
      "Die folgenden Unterlagen lassen sich hier direkt hinterlegen. Jedes Dokument hat seinen eigenen Bereich; was fehlt, bleibt leer.",
  },
  /** Einleitung über der Schadenhistorie (xlsx Z. 7.4b). */
  schaedenIntro: {
    kunde:
      "Bitte je Schaden eine Zeile: Schadenart, Schadentag, Schadenhöhe und eine kurze Bemerkung. Ist der genaue Tag nicht bekannt, tragen Sie bitte einen ungefähren Tag ein und vermerken das in der Bemerkung.",
    intern:
      "Je Schaden eine Zeile: Schadenart, Schadentag, Schadenhöhe und eine kurze Bemerkung. Ist der genaue Tag nicht bekannt, einen ungefähren Tag eintragen und das in der Bemerkung vermerken.",
  },
  /** Hinweis über der Kontaktauswahl auf dem Weg ohne Kontaktbestand. */
  kontaktOhneLookup: {
    kunde:
      "Bitte tragen Sie die Angaben zur Person bzw. Firma ein — wir legen den Kontakt anschließend bei uns an.",
    intern:
      "Angaben zur Person bzw. Firma eintragen; der Kontakt wird beim Übernehmen angelegt.",
  },
} satisfies Record<string, TextVariante>;

export type ImmoTextId = keyof typeof IMMO_TEXTE;

export const immoText = (id: ImmoTextId, ansprache: Ansprache): string =>
  IMMO_TEXTE[id][ansprache];
